import { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { colors } from "../../../styles";
import { useStoreActions, useStoreState } from "../../../store";
import Pricing from "./Pricing";
import Savings from "./SavingsProgram";
import CompanyProfile from "./CompanyProfile";
import AdoptionPlan from "./AdoptionPlan";
import EligibleEmployees from "./EligibleEmployees";
import Payroll from "./Payroll";
import Congrats from "./Congrats";
import logoAtWorkWhite from "../../../assets/logos/logoAtWorkWhite.svg";
import stepOne from "../../../assets/icons/steps/stepOne.svg";
import stepTwo from "../../../assets/icons/steps/stepTwo.svg";
import stepTwoInactive from "../../../assets/icons/steps/stepTwoInactive.svg";
import stepThree from "../../../assets/icons/steps/stepThree.svg";
import stepThreeInactive from "../../../assets/icons/steps/stepThreeInactive.svg";
import stepFour from "../../../assets/icons/steps/stepFour.svg";
import stepFourInactive from "../../../assets/icons/steps/stepFourInactive.svg";
import stepFive from "../../../assets/icons/steps/stepFive.svg";
import stepFiveInactive from "../../../assets/icons/steps/stepFiveInactive.svg";
import stepSix from "../../../assets/icons/steps/stepSix.svg";
import stepSixInactive from "../../../assets/icons/steps/stepSixInactive.svg";
import checkCircle from "../../../assets/icons/greenIcons/checkCircle.svg";
import { ReactComponent as PhoneIcon } from "../../../assets/icons/phoneIcon.svg";
import useGetOnboarding from "../../../hooks/queries/admin/useGetOnboarding";
import Button from "../../../components/Button";
import TalkWithSalesModal from "../../../components/TalkWithSalesModal";
import { adminDashboardFullPath } from "../../../routes/paths";

export type StepComponentProps = {
  nextStep: () => void;
};

const Container = styled("div")(`
    display: flex;
`);

const OnboardingNav = styled("div")(`
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100vh;
    flex: 1.5;
    background: #fff;
    border-right: 1px solid ${colors.gray_01};
    min-width: 480px;
`);

const TopLogo = styled("img")(`
    width: 130px;
    cursor: pointer;
`);

const InnerOnboardingContainer = styled("div")(`
    width: 100%; 
    margin: 80px 0 0 0;
    padding: 0 15% 0 15%;
    height: 85%;
    display: flex;
    flex-direction: column;
`);

const PageBody = styled("div")(`
    height: 100vh;
    flex: 4;
    background: #FCFDFD;
    overflow-y: auto;
    padding: 0 0 80px 0;
`);

const StepsContainer = styled("div")(`
    width: 100%;
    display: table;
    margin: 30px 0 0;
`);

const RowContainer = styled("div")(`
    width: 100%;
    display: flex;
    margin: 0px auto;
    justify-content: start;
`);

const StepLabel = styled("p")(`
    font-family: Inter;
    font-weight: 500;
    font-size: 18px;
    line-height: 22px;
    color: #1E4854;
    margin: 7px 0 0 14px;
    cursor: pointer;
`);

const StepIcon = styled("img")(`
   width: 36px;
`);

const Connector = styled("div")(`
    height: 14px;
    width: 1px;
    background: #C0C0C0; 
    margin: -8px 0 0 18px;
`);

const BoldTitle = styled("h2")(`
    font-family: Inter;
    font-style: normal;
    font-weight: 600;
    font-size: 24px;
    margin: auto;
    letter-spacing: -0.06em;
    color: #1F2635;
`);

const onboardingSteps = [
  {
    step: 1,
    title: "Company Profile",
    Component: CompanyProfile,
    link: "/company-profile",
    icon: stepOne,
    grayIcon: checkCircle,
  },
  {
    step: 2,
    title: "Payroll",
    Component: Payroll,
    link: "/payroll",
    icon: stepTwo,
    grayIcon: stepTwoInactive,
  },
  {
    step: 3,
    title: "Eligible Employees",
    Component: EligibleEmployees,
    link: "/eligible-employees",
    icon: stepThree,
    grayIcon: stepThreeInactive,
  },
  {
    step: 4,
    title: "Savings Program",
    Component: Savings,
    link: "/savings-program",
    icon: stepFour,
    grayIcon: stepFourInactive,
  },
  {
    step: 5,
    title: "Adoption Plan",
    Component: AdoptionPlan,
    link: "/",
    icon: stepFive,
    grayIcon: stepFiveInactive,
  },
  {
    step: 6,
    title: "Pricing",
    Component: Pricing,
    link: "/pricing",
    icon: stepSix,
    grayIcon: stepSixInactive,
  },
];

const Onboarding = () => {
  const modalState = useStoreState((state) => state.modals);
  const modalActions = useStoreActions((actions) => actions.modals);
  const [currentStep, setCurrentStep] = useState<number>(0);

  const { data } = useGetOnboarding();

  const navigate = useNavigate();

  useEffect(() => {
    if (data?.onboarding_status && data.onboarding_status === "complete") {
      navigate(adminDashboardFullPath);
    }
  }, [data, navigate]);

  const outputSteps = (nextStep: () => void) => {
    const Step = onboardingSteps[currentStep].Component;
    //@ts-ignore
    return <Step nextStep={nextStep} />;
  };

  const getRowIcon = (rowItem: any) => {
    if (currentStep === rowItem.step - 1) {
      return rowItem.icon;
    } else if (currentStep >= rowItem.step) {
      return checkCircle;
    }
    return rowItem.grayIcon;
  };

  const rowAction = (index: number, rowItem: any) => {
    // only allow them to go back to previous steps
    if (currentStep > rowItem.step - 1) {
      setCurrentStep(index);
    }
  };

  const nextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const isLastStep = currentStep === onboardingSteps.length;
  return (
    <Container>
      {isLastStep ? (
        <Congrats />
      ) : (
        <>
          <OnboardingNav>
            <InnerOnboardingContainer>
              <div>
                <TopLogo
                  src={logoAtWorkWhite}
                  alt="logo"
                  onClick={() => navigate("/login")}
                />
                <BoldTitle>Emergency Savings Platform</BoldTitle>
              </div>
              <StepsContainer>
                {onboardingSteps.map((rowItem, index) => {
                  return (
                    <Tooltip
                      key={index}
                      title={
                        currentStep < rowItem.step - 1
                          ? "For the best experience, please complete the steps in order."
                          : ""
                      }
                      arrow
                      placement="top-start"
                    >
                      <RowContainer onClick={() => rowAction(index, rowItem)}>
                        <div style={{ display: "table", cursor: "pointer" }}>
                          <StepIcon src={getRowIcon(rowItem)} alt="step" />
                          {index < onboardingSteps.length - 1 && <Connector />}
                        </div>
                        <StepLabel>{rowItem.title}</StepLabel>
                      </RowContainer>
                    </Tooltip>
                  );
                })}
              </StepsContainer>
              <Button
                style={{ marginTop: "auto" }}
                btntype="secondary"
                startIcon={<PhoneIcon />}
                onClick={() => modalActions.setShowContactSalesModal(true)}
              >
                Contact Sales
              </Button>
            </InnerOnboardingContainer>
          </OnboardingNav>
          <PageBody>{outputSteps(nextStep)}</PageBody>
          <TalkWithSalesModal
            modalStatus={modalState.showContactSalesModal}
            closeModal={() => modalActions.setShowContactSalesModal(false)}
          />
        </>
      )}
    </Container>
  );
};

export default Onboarding;
